import { CustomFieldType } from '__types__';
import type { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import type { FieldNameTypeMapping } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import { isRecord } from 'v5/platform/lang/isRecord';

export interface ResourceSearchToV1 {
  /** Each condition in the v1 shape, by filter key; a key with no condition (no values) is absent. */
  filters: Record<string, unknown>;
  /** Keys whose condition has no v1 shape (`contains`, `empty`, two values of a single select…). */
  notConverted: string[];
}

type V1Filter = Record<string, unknown> | null;

/** What every v5 filter is; the query's filters are a union of many, so values are checked here. */
type AnyFilter = { operator: string; value: unknown };

const STRING_CORE_FIELDS = new Set([
  'role',
  'emails',
  'phone_numbers',
  'websites',
  'socials',
  'locations'
]);

const numberOf = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

function numberToV1({ operator, value }: AnyFilter): V1Filter {
  const number = numberOf(value);
  switch (operator) {
    case 'equal':
      return number === null ? null : { eq: number };
    case 'notEqual':
      return number === null ? null : { ne: number };
    case 'greaterThan':
      return number === null ? null : { gt: number };
    case 'greaterOrEqual':
      return number === null ? null : { gte: number };
    case 'lessThan':
      return number === null ? null : { lt: number };
    case 'lessOrEqual':
      return number === null ? null : { lte: number };
    case 'between': {
      const from = isRecord(value) ? numberOf(value.from) : null;
      const to = isRecord(value) ? numberOf(value.to) : null;
      return from === null || to === null ? null : { gte: from, lte: to };
    }
    default:
      return null;
  }
}

function stringToV1({ operator, value }: AnyFilter): V1Filter {
  return operator === 'equals' && typeof value === 'string'
    ? { eq: value }
    : null;
}

function booleanToV1({ operator, value }: AnyFilter): V1Filter {
  return operator === 'equal' && typeof value === 'boolean'
    ? { eq: value }
    : null;
}

/** v1 stores a scalar (a resource type, a single select) as `eq` of its one value. */
function scalarToV1({ operator, value }: AnyFilter): V1Filter {
  return operator === 'matchAny' && Array.isArray(value) && value.length === 1
    ? { eq: value[0] }
    : null;
}

/** `anyOperator`: groups store "any of them" as `eq`, tags and multi-selects as `any`. */
function arrayToV1(
  { operator, value }: AnyFilter,
  anyOperator: 'eq' | 'any'
): V1Filter {
  if (!Array.isArray(value)) return null;
  switch (operator) {
    case 'matchAny':
      return { [anyOperator]: value };
    case 'matchAll':
      return { match: value };
    case 'matchNone':
      return { nmatch: value };
    default:
      return null;
  }
}

function customFieldToV1(
  type: CustomFieldType | undefined,
  filter: AnyFilter
): V1Filter {
  switch (type) {
    case CustomFieldType.SHORT_TEXT:
    case CustomFieldType.LONG_TEXT:
    case CustomFieldType.URL:
      return stringToV1(filter);
    case CustomFieldType.NUMBER:
    case CustomFieldType.PERCENTAGE:
    case CustomFieldType.CURRENCY:
    case CustomFieldType.MEASUREMENT:
    case CustomFieldType.MEASUREMENT_NEW:
    case CustomFieldType.HEIGHT:
      return numberToV1(filter);
    case CustomFieldType.BOOLEAN:
      return booleanToV1(filter);
    case CustomFieldType.SINGLE_SELECT:
      return scalarToV1(filter);
    case CustomFieldType.MULTI_SELECT:
      return arrayToV1(filter, 'any');
    default:
      return null;
  }
}

/** No values picked yet, or archived people left out (the default, not a stored condition). */
function isNoCondition(key: string, { operator, value }: AnyFilter): boolean {
  if (key === 'archived') return operator === 'equal' && value === false;
  if (Array.isArray(value)) {
    return (
      ['matchAny', 'matchAll', 'matchNone'].includes(operator) &&
      value.length === 0
    );
  }
  return value === '';
}

function filterToV1(
  key: string,
  filter: AnyFilter,
  fieldTypes: FieldNameTypeMapping
): V1Filter {
  switch (key) {
    case 'resource_type_id':
      return scalarToV1(filter);
    case 'groups':
      return arrayToV1(filter, 'eq');
    case 'tags':
      return arrayToV1(filter, 'any');
  }
  if (STRING_CORE_FIELDS.has(key)) return stringToV1(filter);
  if (/^custom_\d+$/.test(key)) {
    const type = fieldTypes[key];
    const converted = customFieldToV1(type, filter);
    return converted && { ...converted, custom_field_definition_type: type };
  }
  return null;
}

/**
 * The inverse of `resourceSearchOfV1` for its filters: each v5 condition as the v1 filter a role
 * or a report template stores (tags as names, numbers as numbers, a custom field with its
 * `custom_field_definition_type` from `fieldTypes`). The text search and the sort are not filters
 * and stay out.
 */
export function resourceSearchToV1(
  query: ResourceSearchQuery,
  fieldTypes: FieldNameTypeMapping
): ResourceSearchToV1 {
  const entries = Object.entries(query.filters).flatMap(([key, filter]) =>
    filter && !isNoCondition(key, filter)
      ? [{ key, v1: filterToV1(key, filter, fieldTypes) }]
      : []
  );
  return {
    filters: Object.fromEntries(
      entries.flatMap(({ key, v1 }) => (v1 ? [[key, v1]] : []))
    ),
    notConverted: entries.flatMap(({ key, v1 }) => (v1 ? [] : [key]))
  };
}
